import { hashKey, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { accessRefused } from '../../api/client'

/**
 * Reads this browser was refused, by cache, until a read of each succeeds.
 *
 * Kept beside the cache rather than in a screen, because the screen is the
 * thing that goes: a seal held in a component is lost when the reader leaves
 * the day and comes back, and the answer from before the refusal -- still in
 * the cache -- would be drawn again under the outage that met the return.
 *
 * Only a fetch that succeeds breaks a seal. The cache says which a success
 * was: `setQueryData` marks its write as manual, and a write the browser made
 * to its own cache is not the instance agreeing to answer.
 *
 * And only a fetch **begun after the refusal** breaks it. A request already on
 * its way when the refusal landed was asked before the instance said no, so
 * its answer is no evidence that it would say yes now. Which came first is a
 * count, not a clock: each seal takes the next number, and each fetch carries
 * the number current when it began.
 *
 * A refusal also seals the reads of any family it belongs to -- the same list
 * under other filters, say -- with its own number, so a fetch of a neighbour
 * already on its way when it came cannot hand that neighbour back either.
 *
 * And it seals the reads made under it, with the same number, at the moment it
 * is recorded: a read names what is made under it once, and that is kept here,
 * so a refusal that lands after the reader has left -- or a refused write that
 * names the read -- reaches them as well as one met on screen.
 */
type Seals = {
  /** Sealed keys, by hash, with the number each seal took. */
  exact: Map<string, number>
  /** The number of the latest seal. */
  count: number
  /** By hash: the number current when that key's latest fetch began. */
  begun: Map<string, number>
  /**
   * Told when a read is sealed by something no change to its own state
   * announces: a refused write, a refused neighbour, a refused attempt with a
   * retry still out.
   */
  listeners: Set<() => void>
  /** Key prefixes whose reads stand or fall together, by their hash. */
  families: Map<string, readonly unknown[]>
  /** By a read's hash: the hashes of the reads made under it. */
  unders: Map<string, Set<string>>
}

const seals = new WeakMap<QueryClient, Seals>()

function seal(held: Seals, ids: readonly string[]): number {
  held.count += 1
  for (const id of ids) {
    held.exact.set(id, held.count)
    sealUnder(held, id, held.count)
  }
  return held.count
}

/**
 * Seal the reads made under `id` as of seal `at`, and theirs under them. A
 * read sealed already by a later refusal keeps that one.
 */
function sealUnder(held: Seals, id: string, at: number): void {
  for (const child of held.unders.get(id) ?? []) {
    if ((held.exact.get(child) ?? -1) >= at) continue
    held.exact.set(child, at)
    sealUnder(held, child, at)
  }
}

function inFamily(key: readonly unknown[], family: readonly unknown[]): boolean {
  return key.length >= family.length && hashKey(key.slice(0, family.length)) === hashKey(family)
}

/**
 * Seal every read the cache holds in each family `key` belongs to, as of seal
 * `at`. A read sealed already by a later refusal keeps that one.
 */
function sealFamily(client: QueryClient, held: Seals, key: readonly unknown[], at: number): void {
  for (const family of held.families.values()) {
    if (!inFamily(key, family)) continue
    for (const query of client.getQueryCache().findAll({ queryKey: family })) {
      if ((held.exact.get(query.queryHash) ?? -1) < at) held.exact.set(query.queryHash, at)
    }
  }
}

function sealsOf(client: QueryClient): Seals {
  let held = seals.get(client)
  if (!held) {
    const fresh: Seals = {
      exact: new Map(),
      count: 0,
      begun: new Map(),
      listeners: new Set(),
      families: new Map(),
      unders: new Map(),
    }
    client.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return
      const { action } = event
      const id = event.query.queryHash
      if (action.type === 'fetch') fresh.begun.set(id, fresh.count)
      // A failed attempt with a retry still to come is a refusal as much as the
      // last one: what the screen holds is no longer the reader's to see while
      // the retry is out, and the retry may end in an outage instead.
      if ((action.type === 'error' || action.type === 'failed') && accessRefused(action.error)) {
        // A refusal seals again even what is sealed, and its family with it: a
        // fetch already on its way when it came is as old as the refusal says,
        // whichever read of the family it was. Only a refusal the cache saw
        // arrive takes a new number; a refusal drawn again at render does not.
        sealFamily(client, fresh, event.query.queryKey, seal(fresh, [id]))
        // Neither a neighbour sealed with it nor a read whose retry is still out
        // hears of it from the cache, so they are told here.
        for (const told of fresh.listeners) told()
      }
      if (action.type === 'success' && !action.manual) {
        // A fetch whose start was never seen began before anything here was
        // watching, so it counts as begun before every seal.
        if ((fresh.begun.get(id) ?? -1) >= (fresh.exact.get(id) ?? Infinity)) fresh.exact.delete(id)
      }
    })
    seals.set(client, fresh)
    held = fresh
  }
  return held
}

/**
 * How many refusals this cache has been told of, read or write: a number that
 * only grows. Work begun under one count and finishing under a higher one has
 * seen the instance refuse this browser something in between, whatever has
 * been read again since.
 */
export function refusalCount(client: QueryClient): number {
  return sealsOf(client).count
}

/**
 * Seal reads because the instance refused a write on what they show.
 *
 * A 401 or 403 on a write says the same thing about the reader as one on a
 * read: what the screen holds of the record is no longer theirs to see. So
 * the reads it names are sealed at once, as a refused read would seal them,
 * and come back only on a read of each that begins after this and succeeds.
 */
export function sealRefusedWrite(client: QueryClient, keys: readonly (readonly unknown[])[]): void {
  const held = sealsOf(client)
  seal(
    held,
    keys.map((key) => hashKey(key)),
  )
  for (const told of held.listeners) told()
}

/**
 * A read's answer, for as long as the instance still allows it to be shown.
 *
 * React Query keeps the last good answer beside a failed refetch, which is
 * right for an outage and wrong for a refusal. So a refusal seals the key, and
 * only a read of that key that succeeds after the refusal breaks the seal. A
 * 503, a dropped connection or a return to the screen after it establishes
 * nothing about whether this reader may see the answer, so none of them hands
 * it back. A failure with no refusal before it leaves the answer standing,
 * for the screen to say it may be old.
 *
 * `under` names reads made under this one: a refusal of a day seals its
 * entries too, so the read that brings the day back cannot bring back the
 * entries read before the refusal. They wait for a read of their own.
 *
 * `family` names a key prefix this read shares with its neighbours -- the same
 * list under other filters, say. A refusal of any read in it seals every read
 * of it the cache holds, so going back to a filter answered before the
 * refusal shows nothing until that filter is read again and answered.
 *
 * A sealed read with no failure standing against it -- the cache was written
 * to, say -- has nothing to show and nothing to retry, so it is asked again,
 * when `ask` allows it: a read made under a refused one is not.
 */
export function useSealedRead<T>(
  query: { data: T | undefined; error: unknown; isFetching: boolean; refetch: () => unknown },
  queryKey: readonly unknown[],
  {
    under = [],
    family,
    ask = true,
  }: {
    under?: readonly (readonly unknown[])[]
    family?: readonly unknown[]
    ask?: boolean
  } = {},
): { data: T | undefined; sealed: boolean } {
  const client = useQueryClient()
  const held = sealsOf(client)
  const id = hashKey(queryKey)
  if (family) held.families.set(hashKey(family), family)
  if (under.length > 0) {
    const named = held.unders.get(id) ?? new Set<string>()
    for (const other of under) named.add(hashKey(other))
    held.unders.set(id, named)
  }
  // A write's refusal changes nothing in the cache, so it says so itself.
  useSyncExternalStore(
    useCallback(
      (told: () => void) => {
        held.listeners.add(told)
        return () => void held.listeners.delete(told)
      },
      [held],
    ),
    () => held.exact.get(id) ?? 0,
  )
  if (accessRefused(query.error)) {
    // A seal takes a number, so it is set once: the refusal the cache already
    // told the subscription about is sealed by now, and this catches one it
    // could not have, from before it was watching.
    const at = held.exact.get(id) ?? seal(held, [id])
    // The reads made under this one are sealed from the same refusal, so
    // they take its number: a read of theirs begun before it cannot break
    // them, and one begun after can.
    sealUnder(held, id, at)
    sealFamily(client, held, queryKey, at)
  }
  const sealed = held.exact.has(id)
  const unanswered = sealed && ask && query.error === null && !query.isFetching
  const { refetch } = query
  useEffect(() => {
    if (unanswered) void refetch()
  }, [unanswered, refetch])
  return { data: sealed ? undefined : query.data, sealed }
}
